import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Link2, RefreshCw, Trash2, Globe } from "lucide-react";
import { toast } from "sonner";

export default function ChannelManager() {
    const { activeHotelId } = useAuth();
    const [supported, setSupported] = useState([]);
    const [connected, setConnected] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [openConnect, setOpenConnect] = useState(false);
    const [openSync, setOpenSync] = useState(false);
    const [selectChan, setSelectChan] = useState(null);
    const [propId, setPropId] = useState("");
    const [syncForm, setSyncForm] = useState({ room_type_id: "", rate: 0, availability: 5, channels: [] });
    const [roomTypes, setRoomTypes] = useState([]);
    const [syncLog, setSyncLog] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [openInbound, setOpenInbound] = useState(false);
    const [inbForm, setInbForm] = useState({ channel_id: "", guest_name: "", guest_email: "", guest_phone: "", check_in_date: "", check_out_date: "", room_type_id: "", adults: 2, children: 0, total_amount: 5000, channel_reference: "" });

    const load = async () => {
        if (!activeHotelId) return;
        try {
            const [c, r, l, b] = await Promise.all([
                api.get(`/ota/channels?hotel_id=${activeHotelId}`),
                api.get(`/rooms/types?hotel_id=${activeHotelId}`),
                api.get(`/ota/sync-log?hotel_id=${activeHotelId}`),
                api.get(`/ota/channel-bookings?hotel_id=${activeHotelId}`),
            ]);
            setSupported(c.data.supported); setConnected(c.data.connected); setRoomTypes(r.data); setSyncLog(l.data); setBookings(b.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [activeHotelId]);

    const connect = async () => {
        try {
            await api.post("/ota/connect", { hotel_id: activeHotelId, channel_id: selectChan.id, property_id: propId });
            toast.success(`${selectChan.name} connected (MOCKED)`); setOpenConnect(false); setSelectChan(null); setPropId(""); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const disconnect = async (id) => {
        try { await api.delete(`/ota/connect/${id}`); toast.success("Disconnected"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const submitSync = async () => {
        try {
            await api.post("/ota/sync-rates", {
                hotel_id: activeHotelId, room_type_id: syncForm.room_type_id,
                rate: Number(syncForm.rate), availability: Number(syncForm.availability),
                channels: syncForm.channels,
            });
            toast.success(`Synced to ${syncForm.channels.length} channel(s)`); setOpenSync(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const connectedIds = new Set(connected.map(c => c.channel_id));

    const submitInbound = async () => {
        try {
            const { data } = await api.post("/ota/inbound-booking", {
                ...inbForm, hotel_id: activeHotelId,
                adults: Number(inbForm.adults), children: Number(inbForm.children),
                total_amount: Number(inbForm.total_amount),
                channel_reference: inbForm.channel_reference || `ref-${Date.now()}`,
            });
            toast.success(`Booking received from ${data.channel} — Room ${data.room_number} (commission ₹${data.commission})`);
            setOpenInbound(false);
            setInbForm({ ...inbForm, guest_name: "", guest_email: "", guest_phone: "" });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="OTA Channel Manager" subtitle={`${connected.length} of ${supported.length} channels connected (SIMULATED)`}>
            <Card className="p-4 mb-4 border border-amber-200 bg-amber-50 rounded-md">
                <div className="text-sm text-amber-900">
                    <strong>Simulated Channel Manager.</strong> Real Booking.com / Expedia / MakeMyTrip integrations require commercial partner accounts. This module simulates the sync workflow — connections and rate/availability pushes are logged and visible in the sync log below.
                </div>
            </Card>

            <div className="flex justify-between items-center mb-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Available Channels</div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setOpenInbound(true)} disabled={!connected.length} className="rounded-sm" data-testid="simulate-booking-btn">
                        Simulate Inbound Booking
                    </Button>
                    <Button className="bg-[#0A1C3E] rounded-sm" onClick={() => setOpenSync(true)} disabled={!connected.length} data-testid="sync-rates-btn">
                        <RefreshCw className="w-4 h-4 mr-1" /> Sync Rates & Availability
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                {supported.map(ch => {
                    const conn = connected.find(c => c.channel_id === ch.id);
                    return (
                        <Card key={ch.id} className={`p-4 border rounded-md shadow-none ${conn ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`} data-testid={`channel-${ch.id}`}>
                            <div className="flex justify-between items-start">
                                <Globe className="w-4 h-4 text-slate-500" />
                                {conn ? (
                                    <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded uppercase tracking-wider font-semibold">Connected</span>
                                ) : (
                                    <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded uppercase tracking-wider">Available</span>
                                )}
                            </div>
                            <div className="mt-3 font-semibold text-slate-900">{ch.name}</div>
                            <div className="text-xs text-slate-500 mt-1">Commission: {ch.commission}%</div>
                            {conn?.last_sync && <div className="text-[10px] text-emerald-700 mt-1">Last sync: {conn.last_sync.slice(0, 16).replace("T", " ")}</div>}
                            <div className="mt-3">
                                {conn ? (
                                    <button onClick={() => disconnect(conn.id)} className="text-xs text-red-600 hover:underline" data-testid={`disconnect-${ch.id}`}>Disconnect</button>
                                ) : (
                                    <button onClick={() => { setSelectChan(ch); setOpenConnect(true); }} className="text-xs text-[#0A1C3E] font-semibold hover:underline" data-testid={`connect-${ch.id}`}>
                                        <Link2 className="w-3 h-3 inline mr-1" /> Connect
                                    </button>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Sync History</div>
            <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                        <tr><th className="px-4 py-3">Timestamp</th><th className="px-4 py-3">Channels</th><th className="px-4 py-3">Rate</th><th className="px-4 py-3">Availability</th><th className="px-4 py-3">Status</th></tr>
                    </thead>
                    <tbody>
                        {syncLog.map(s => (
                            <tr key={s.id} className="border-b border-slate-100">
                                <td className="px-4 py-3 text-xs">{s.synced_at?.slice(0, 19).replace("T", " ")}</td>
                                <td className="px-4 py-3 text-xs">{s.channels?.join(", ")}</td>
                                <td className="px-4 py-3 text-sm font-mono">₹{Number(s.rate).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm font-mono">{s.availability}</td>
                                <td className="px-4 py-3"><span className="status-badge bg-emerald-100 text-emerald-800 border-emerald-200">{s.status}</span></td>
                            </tr>
                        ))}
                        {syncLog.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-sm text-slate-500">No sync history yet</td></tr>}
                    </tbody>
                </table>
            </Card>

            <Dialog open={openConnect} onOpenChange={setOpenConnect}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Connect {selectChan?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs uppercase tracking-wider">Property ID / Hotel Code</Label><Input value={propId} onChange={e => setPropId(e.target.value)} placeholder="From channel dashboard" data-testid="prop-id" /></div>
                        <div className="text-xs text-slate-500">Commission: {selectChan?.commission}%. Connection is simulated for demo purposes.</div>
                    </div>
                    <DialogFooter><Button onClick={connect} className="bg-[#0A1C3E]" data-testid="connect-submit">Connect</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={openSync} onOpenChange={setOpenSync}>                <DialogContent>
                    <DialogHeader><DialogTitle>Sync Rates & Availability</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs uppercase tracking-wider">Room Type</Label>
                            <Select value={syncForm.room_type_id} onValueChange={v => setSyncForm({ ...syncForm, room_type_id: v })}>
                                <SelectTrigger data-testid="sync-room-type"><SelectValue placeholder="Select room type" /></SelectTrigger>
                                <SelectContent>{roomTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · ₹{t.base_rate}</SelectItem>)}</SelectContent>
                            </Select></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs uppercase tracking-wider">Rate (₹)</Label><Input type="number" value={syncForm.rate} onChange={e => setSyncForm({ ...syncForm, rate: e.target.value })} data-testid="sync-rate" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Available Rooms</Label><Input type="number" value={syncForm.availability} onChange={e => setSyncForm({ ...syncForm, availability: e.target.value })} /></div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider">Push to Channels</Label>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {connected.map(c => (
                                    <button key={c.id} onClick={() => setSyncForm(f => ({ ...f, channels: f.channels.includes(c.channel_id) ? f.channels.filter(x => x !== c.channel_id) : [...f.channels, c.channel_id] }))}
                                        className={`px-3 py-1.5 text-xs rounded-sm border ${syncForm.channels.includes(c.channel_id) ? "bg-[#0A1C3E] text-white border-[#0A1C3E]" : "bg-white border-slate-200"}`}
                                        data-testid={`sync-select-${c.channel_id}`}>
                                        {c.channel_name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter><Button onClick={submitSync} disabled={!syncForm.room_type_id || !syncForm.channels.length} className="bg-[#0A1C3E]" data-testid="sync-submit">Push Sync</Button></DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Channel Bookings */}
            {bookings.length > 0 && (
                <>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3 mt-6">Channel Bookings ({bookings.length})</div>
                    <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none mb-6">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                                <tr><th className="px-4 py-3">Channel</th><th className="px-4 py-3">Guest</th><th className="px-4 py-3">Dates</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3">Reference</th></tr>
                            </thead>
                            <tbody>
                                {bookings.map(b => (
                                    <tr key={b.id} className="border-b border-slate-100">
                                        <td className="px-4 py-3"><span className="status-badge bg-blue-100 text-blue-800 border-blue-200">{b.channel_name || b.source}</span></td>
                                        <td className="px-4 py-3 text-sm">{b.guest_name}</td>
                                        <td className="px-4 py-3 text-xs">{b.check_in_date?.slice(0, 10)} → {b.check_out_date?.slice(0, 10)}</td>
                                        <td className="px-4 py-3 text-sm font-mono">₹{Number(b.total).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-sm font-mono text-red-600">−₹{Number(b.commission_amount).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-xs font-mono text-slate-500">{b.channel_reference}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </>
            )}

            {/* Inbound Booking Dialog */}
            <Dialog open={openInbound} onOpenChange={setOpenInbound}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Simulate Inbound Booking</DialogTitle></DialogHeader>
                    <div className="text-xs text-slate-500 mb-3">In production, this arrives as a webhook from Booking.com/Expedia. This form simulates that event for testing.</div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label className="text-xs uppercase tracking-wider">Channel</Label>
                            <Select value={inbForm.channel_id} onValueChange={v => setInbForm({ ...inbForm, channel_id: v })}>
                                <SelectTrigger data-testid="inb-channel"><SelectValue placeholder="Select channel" /></SelectTrigger>
                                <SelectContent>{connected.map(c => <SelectItem key={c.id} value={c.channel_id}>{c.channel_name}</SelectItem>)}</SelectContent>
                            </Select></div>
                        <div><Label className="text-xs uppercase tracking-wider">Guest Name</Label><Input value={inbForm.guest_name} onChange={e => setInbForm({ ...inbForm, guest_name: e.target.value })} data-testid="inb-guest" /></div>
                        <div><Label className="text-xs uppercase tracking-wider">Guest Phone</Label><Input value={inbForm.guest_phone} onChange={e => setInbForm({ ...inbForm, guest_phone: e.target.value })} /></div>
                        <div><Label className="text-xs uppercase tracking-wider">Check-in</Label><Input type="date" value={inbForm.check_in_date} onChange={e => setInbForm({ ...inbForm, check_in_date: e.target.value })} data-testid="inb-checkin" /></div>
                        <div><Label className="text-xs uppercase tracking-wider">Check-out</Label><Input type="date" value={inbForm.check_out_date} onChange={e => setInbForm({ ...inbForm, check_out_date: e.target.value })} /></div>
                        <div><Label className="text-xs uppercase tracking-wider">Room Type</Label>
                            <Select value={inbForm.room_type_id} onValueChange={v => setInbForm({ ...inbForm, room_type_id: v })}>
                                <SelectTrigger><SelectValue placeholder="Auto-assign" /></SelectTrigger>
                                <SelectContent>{roomTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select></div>
                        <div><Label className="text-xs uppercase tracking-wider">Total (₹)</Label><Input type="number" value={inbForm.total_amount} onChange={e => setInbForm({ ...inbForm, total_amount: e.target.value })} data-testid="inb-amount" /></div>
                    </div>
                    <DialogFooter><Button onClick={submitInbound} disabled={!inbForm.channel_id || !inbForm.guest_name || !inbForm.check_in_date || !inbForm.check_out_date} className="bg-[#0A1C3E]" data-testid="inb-submit">Receive Booking</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
